import React, { useRef, useState } from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { useToast } from "components/toast";

/**
 * TermsEditor — written terms & conditions override, edited in place
 * (Session 59, WO_INVOICE_TAB_AND_TERMS.md Part B; mockup 35).
 *
 * Renders the effective terms prose for one record (quote / invoice /
 * purchase order) and, for staff, an "Edit terms" affordance that opens a
 * pre-populated content box. Save writes a per-record override; Reset
 * clears it (record falls back to the company default); Cancel discards.
 *
 * This is a SECOND entry point to one per-record override — the same
 * editor mounts inside the record's WO tab AND on the record's own view.
 * Both call the same update fn (updateQuote / updateInvoice /
 * updatePurchaseOrder), so there is one override per record regardless of
 * where it was set.
 *
 * The record carries two fields from the backend:
 *   - `termsOverride` — the RAW per-record override (null when none set).
 *     Distinguishes "custom override" from "company default", and
 *     pre-fills the box with the override when one exists.
 *   - `resolvedTerms` — the effective, MERGE-TAG-SUBSTITUTED text that
 *     actually renders on the PDF / client view. Shown in the preview.
 *
 * Merge tags ({{net_days}} / {{due_date}} / {{payment_terms}}) stay
 * literal in the editor (they resolve at render, server-side, inside
 * resolveTerms()). The inserter drops a token at the cursor.
 *
 * WHERE terms show is unchanged — this component never renders on the
 * client-facing PDF; it is a staff editing surface. Callers gate it on
 * staff (isCompany) themselves.
 */

const MERGE_TAGS = ["{{net_days}}", "{{due_date}}", "{{payment_terms}}", "{{fuel_surcharge_notice}}"];

type UpdateFn = (args: { data: { id: string }; update: any }) => Promise<any>;

export default function TermsEditor({
  docType,
  record,
  update,
  onSaved,
  headingLabel,
}: {
  docType: "quote" | "invoice" | "po";
  record: { id: string; termsOverride?: string | null; resolvedTerms?: string | null };
  update: UpdateFn;
  onSaved: () => void | Promise<void>;
  // Optional label suffix for the "Save to <X>" button + edit heading
  // (e.g. "invoice", "quote", "purchase order"). Defaults from docType.
  headingLabel?: string;
}) {
  const toast = useToast();
  const label = headingLabel ?? (docType === "po" ? "purchase order" : docType);

  const hasOverride =
    typeof record?.termsOverride === "string" && record.termsOverride.trim().length > 0;

  const [editing, setEditing] = useState(false);
  const [text, setText] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Pre-fill the box with the override if one exists, else the resolved
  // company default — so staff edit rather than retype (mockup 35).
  const startEditing = () => {
    setText(hasOverride ? (record.termsOverride as string) : record.resolvedTerms || "");
    setEditing(true);
  };

  const insertTag = (tag: string) => {
    const el = textareaRef.current;
    if (!el) {
      setText((t) => t + tag);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + tag + text.slice(end);
    setText(next);
    // Restore focus + place the cursor after the inserted tag.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.add(
        "Terms cannot be empty. Use Reset to default instead.",
        "var(--theme-ui-colors-danger)",
      );
      return;
    }
    setSaving(true);
    const res = await update({ data: { id: record.id }, update: { termsOverride: trimmed } });
    setSaving(false);
    if (res?.id) {
      toast.add("Terms saved to this record.", "var(--theme-ui-colors-success)");
      setEditing(false);
      await onSaved();
    } else {
      toast.add(res?.message || "Could not save the terms.", "var(--theme-ui-colors-danger)");
    }
  };

  const reset = async () => {
    setSaving(true);
    const res = await update({ data: { id: record.id }, update: { termsOverride: null } });
    setSaving(false);
    if (res?.id) {
      toast.add("Terms reset to the company default.", "var(--theme-ui-colors-success)");
      setEditing(false);
      await onSaved();
    } else {
      toast.add(res?.message || "Could not reset the terms.", "var(--theme-ui-colors-danger)");
    }
  };

  const labelSx = {
    fontFamily: "Poppins-Medium, Poppins, sans-serif",
    fontSize: "8px",
    letterSpacing: "0.5px",
    color: themevals.colors.dkgrey,
    textTransform: "uppercase" as const,
    marginBottom: "6px",
  };

  if (!editing) {
    return (
      <Box>
        <Flex sx={{ alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={labelSx}>Terms &amp; conditions</Box>
            <P
              color={themevals.colors.dkgrey}
              marginBottom="0"
              marginTop="0"
              fontFamily="Poppins"
              fontSize="10px"
              lineHeight="14px"
            >
              {hasOverride ? "Custom override on this record" : "Company default (no override)"}
            </P>
          </Box>
          <BasicButton
            type="button"
            scheme="secondary"
            styles={{ margin: "0", flexShrink: "0" }}
            keepText={true}
            onClick={startEditing}
          >
            Edit terms
          </BasicButton>
        </Flex>

        <Box
          sx={{
            marginTop: "10px",
            padding: "12px 14px",
            borderRadius: "8px",
            background: "#FBFBF9",
            border: `1px solid ${themevals.colors.inputOutline}`,
          }}
        >
          <P
            color={themevals.colors.dkgrey}
            marginBottom="0"
            marginTop="0"
            fontFamily="Poppins"
            fontSize="10px"
            lineHeight="16px"
            style={{ whiteSpace: "pre-wrap" }}
          >
            {record?.resolvedTerms || ""}
          </P>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={labelSx}>Edit {label} terms &amp; conditions</Box>
      <P
        color={themevals.colors.dkgrey}
        marginBottom="10px"
        marginTop="0"
        fontFamily="Poppins"
        fontSize="10px"
        lineHeight="14px"
      >
        Overrides the terms text on THIS {label} only. Merge tags stay live; they resolve
        from the payment-term fields when the {label} renders.
      </P>

      <Flex sx={{ gap: "12px", flexWrap: "wrap", alignItems: "stretch" }}>
        <Box sx={{ flex: "1 1 420px", minWidth: "280px" }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{
              width: "100%",
              minHeight: "120px",
              resize: "vertical",
              padding: "10px 12px",
              borderRadius: "6px",
              border: `1.5px solid ${themevals.colors.primary}`,
              fontFamily: "Poppins",
              fontSize: "11px",
              lineHeight: "17px",
              color: "#444441",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "0 0 176px",
            padding: "12px",
            borderRadius: "8px",
            background: "#F4F9FF",
            border: "1px solid #CFE3F8",
          }}
        >
          <Box sx={{ ...labelSx, color: "#185FA5", marginBottom: "8px" }}>Insert merge tag</Box>
          <Flex sx={{ flexDirection: "column", gap: "6px" }}>
            {(docType === "quote" ? MERGE_TAGS : MERGE_TAGS.filter((t) => t !== "{{fuel_surcharge_notice}}")).map((tag) => (
              <Box
                key={tag}
                as="button"
                {...({ type: "button" } as any)}
                onClick={() => insertTag(tag)}
                sx={{
                  textAlign: "left",
                  background: "#FFFFFF",
                  border: "1px solid #CFE3F8",
                  borderRadius: "5px",
                  padding: "5px 8px",
                  cursor: "pointer",
                  fontFamily: "Poppins",
                  fontSize: "10px",
                  color: "#3D6A93",
                }}
              >
                {tag}
              </Box>
            ))}
          </Flex>
          <P
            color="#7FA6CB"
            marginBottom="0"
            marginTop="8px"
            fontFamily="Poppins"
            fontSize="9px"
            lineHeight="12px"
          >
            Resolve from the payment fields at render.
          </P>
        </Box>
      </Flex>

      <Flex sx={{ gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
        <BasicButton
          type="button"
          scheme="primary"
          styles={{ margin: "0" }}
          $submitting={saving}
          keepText={true}
          onClick={save}
        >
          Save to {label}
        </BasicButton>
        <BasicButton
          type="button"
          scheme="secondary"
          styles={{ margin: "0" }}
          $submitting={saving}
          keepText={true}
          onClick={reset}
        >
          Reset to default
        </BasicButton>
        <BasicButton
          type="button"
          scheme="secondary"
          styles={{ margin: "0" }}
          keepText={true}
          onClick={() => setEditing(false)}
        >
          Cancel
        </BasicButton>
      </Flex>
    </Box>
  );
}
